// Dependencies
import React from 'react';

export type size = 'default' | 'medium' | 'small' | 'circle';

export enum variant {
  Open = 'open',
  Managed = 'managed',
  PendingWork = 'pending-work',
  WorkInProgress = 'work-in-progress',
  // Estados propios del RV (2026-07-20): antes se mapeaban a
  // pending-work/work-in-progress y el operario veía labels de despacho
  // ("Pendiente de trabajo") en un recorrido pausado. 2026-08-05: `InProgress`
  // tiene además texto ("En progreso") y color (`blueRouteInProgress`)
  // propios — hasta entonces los copiaba de `WorkInProgress` y las dos pills
  // eran indistinguibles pese a ser estados de máquinas distintas.
  InProgress = 'in-progress',
  Paused = 'paused',
  PendingValidation = 'pending-validation',
  PendingATValidation = 'pending-at-validation', // T66: Estado para validación A.T
  Validated = 'validated',
  PendingCertification = 'pending-certification', // Actions: post-validación, pendiente de cobro a Generalitat
  Partial = 'partial',
  End = 'end',
  Annulled = 'annulled',
  SurveillanceRoute = 'surveillanceRoute',
  Campaign = 'campaign',
  Duplicate = 'duplicate',
  Roid = 'roid',
  Default = 'default',
  Transfer = 'transfer',
  /**
   * 🔴 NO confundir con el estado `OrderState.Transferred`, que se RETIRÓ el
   * 2026-08-31 por no haberse escrito nunca. Esta variante sigue viva y es
   * otra cosa: la pintan las cards de ShowOT cuando `isTransferred === true`
   * —el flag real del traspaso, 222 órdenes en producción— para enseñar la
   * pastilla verde «Transferida». Su etiqueta `common:states.transferred`
   * existe en los tres idiomas y NO es una clave muerta.
   */
  Transferred = 'transferred',
  // P15 Task 1 (2026-07-09) — cross-links del Recorregut de Vigilància (spec
  // REQUISITOS_RECORRIDO_VIGILANCIA_V4.md líneas 266/709/1019). Reemplazan el
  // `LinkedPill` gris ad-hoc de `RouteSegmentOT`. Verde = vínculo ya resuelto
  // (mismo semantic que `Transferred`); ámbar = pendiente, informativo
  // (mismo tono que usaba el `LinkedPill kind="pending"` que sustituyen);
  // garnet = CTA de acción (mismo semantic que `Transfer`).
  LinkedToSegment = 'linkedToSegment',
  LinkedFromSegment = 'linkedFromSegment',
  PendingLink = 'pendingLink',
  LinkCta = 'linkCta',
  /**
   * Spec aviso por zona / vinculación § 16.3: la etiqueta de la card de un
   * trabajo hecho por una patrulla SIN vincular («Treball de Su.NN · sense
   * vincular»). Ámbar como lo pendiente, pero con texto BLANCO como las demás
   * pills de vínculo que se pulsan (revisión del usuario 28/09): el negro de
   * `PendingLink` es para el aviso informativo del tramo, que no tiene menú.
   */
  UnlinkedWork = 'unlinkedWork',
  /**
   * De qué Recorregut de Vigilància viene una Actuació bàsica. Es un chip
   * CLICABLE que lleva a su detalle, así que va en garnet con texto blanco —
   * el mismo tratamiento que «Transferir» o «Vincular a recorregut», que son
   * los otros chips de la app que te llevan a algún sitio.
   *
   * Su etiqueta sale de la variante (como `surveillanceRoute`) y el ROID lo
   * aporta el consumidor por `title` (como `campaign`): «Recorregut de
   * Vigilància Su.NNN» en escritorio, «RV Su.NNN» en tablet.
   */
  FromRoute = 'fromRoute',
}

export type tagProps = React.HTMLAttributes<HTMLDivElement> & {
  variant: variant;
  children?: string;
  roid?: string;
  sizeTag?: size;
  /**
   * P15 Task 1 — slot opcional para mini-iconos de acción (ver/editar)
   * renderizado DENTRO del tag, alineado tras el texto. Pensado para las
   * variants de vínculo (`LinkedToSegment`/`LinkedFromSegment`/`LinkCta`),
   * compuesto por el consumidor con `<TagActionIcon icon="view"|"edit" .../>`.
   * Aditivo: ningún uso existente de `SpecialTag` lo pasa hoy, así que no
   * afecta su render.
   */
  actions?: React.ReactNode;
};

export type ContainerProps = {
  variant: variant;
  sizeTag: size;
};

// P15 opción C (2026-07-10) — acciones de la pill de vínculo servidas desde
// un MENÚ anclado con filas táctiles (los mini-iconos inline de 16px eran
// demasiado pequeños para tablet — feedback usuario).
export type tagMenuActionIcon = 'view' | 'edit' | 'unlink' | 'link';

export type tagMenuAction = {
  icon: tagMenuActionIcon;
  /** Texto de la fila del menú (i18n lo aporta el consumidor). También es la
   * etiqueta accesible de la acción cuando hay una sola (tap directo). */
  label: string;
  onClick: () => void;
};

export type tagActionsMenuProps = {
  variant: variant;
  sizeTag?: size;
  title?: string;
  /**
   * 1 acción → la pill entera la ejecuta al tap (target máximo, sin menú ni
   * caret). 2+ → la pill despliega el menú anclado con una fila táctil
   * (≥4.4rem) por acción.
   */
  actions: tagMenuAction[];
  /** `data-testid` de la pill (para la E2E). */
  testId?: string;
};
